import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ShieldCheck,
  MapPin,
  Sparkles,
  ArrowRight,
  Star,
  Phone,
  Search,
  MessageSquare,
  Car,
  PartyPopper,
  Heart,
  Briefcase,
  CalendarDays,
} from "lucide-react";
import heroImg from "@/assets/hero.webp";
import { usd } from "@/lib/pricing";
import { optimizeImg, srcSet } from "@/lib/img";

const homeQuery = queryOptions({
  queryKey: ["home"],
  queryFn: async () => {
    const [carsRes, settingsRes] = await Promise.all([
      supabase.from("cars").select("*").eq("active", true)
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false }).limit(3),
      supabase.from("settings").select("contact_phone").eq("id", 1).maybeSingle(),
    ]);
    if (carsRes.error) throw carsRes.error;
    return { cars: carsRes.data ?? [], phone: settingsRes.data?.contact_phone ?? null };
  },
});

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Luxury Car Rental Miami | Exotic & SUV Rentals | Creed" },
      { name: "description", content: "Miami's boutique luxury car rental. Hand-picked exotics, SUVs & convertibles delivered to MIA, hotels & Airbnbs. Insurance included, no hidden fees." },
      { property: "og:title", content: "Luxury Car Rental Miami | Exotic & SUV Rentals | Creed" },
      { property: "og:description", content: "Hand-picked exotics & luxury SUVs, delivered anywhere in Miami-Dade." },
      { property: "og:url", content: "https://creedcarental.com/" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `https://creedcarental.com${heroImg}` },
      { property: "og:image:width", content: "1920" },
      { property: "og:image:height", content: "1080" },
      { property: "og:image:alt", content: "Luxury rental car at sunset in Miami" },
      { name: "twitter:image", content: `https://creedcarental.com${heroImg}` },
    ],
    links: [
      { rel: "canonical", href: "https://creedcarental.com/" },
      { rel: "preload", as: "image", href: heroImg, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Creed Car Rental",
          url: "https://creedcarental.com/",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://creedcarental.com/fleet?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(homeQuery),
  component: Home,
  errorComponent: () => <ErrorView />,
  notFoundComponent: () => <ErrorView />,
});

function ErrorView() {
  return <div className="p-10 text-center">Something went wrong.</div>;
}

function Home() {
  const { data: { cars, phone } } = useSuspenseQuery(homeQuery);
  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={heroImg} alt="Luxury rental car at sunset in Miami" width={1920} height={1080} fetchPriority="high" decoding="async" className="h-full w-full object-cover object-[70%_center] md:object-[right_center]" />
          <div className="absolute inset-0 bg-gradient-to-b from-espresso/55 via-espresso/25 to-cream md:bg-gradient-to-r md:from-espresso/75 md:via-espresso/35 md:to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-cream" />

          <svg
            aria-hidden
            className="absolute right-6 top-24 h-40 w-40 opacity-25 md:right-16 md:h-56 md:w-56"
            viewBox="0 0 100 100"
            fill="none"
            stroke="oklch(0.72 0.15 65)"
            strokeWidth="0.5"
          >
            <circle cx="50" cy="50" r="40" />
            <circle cx="50" cy="50" r="30" />
            <circle cx="50" cy="50" r="20" />
            <path d="M5 50 H95 M50 5 V95" />
          </svg>
        </div>
        <div className="mx-auto max-w-7xl px-6 pt-24 pb-20 text-cream md:pt-28 md:pb-40">
          <div className="max-w-2xl">
            <div className="text-sm uppercase tracking-[0.3em] text-accent">Owner-operated · Miami</div>
            <h1 className="mt-4 font-display text-5xl leading-tight md:text-7xl">Luxury cars, delivered by the owner.</h1>
            <p className="mt-6 max-w-xl text-lg text-cream/90 leading-relaxed">
              A small Miami fleet I run myself — not a faceless app, not a rental counter. Pick the car that fits your trip, send me your dates, and I'll bring it to you cleaned, fueled, and ready to go. Insurance is included in every quote, and you'll never hit a surprise fee at pickup.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-accent text-espresso hover:bg-accent/90">
                <Link to="/fleet">Browse the fleet <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              {phone ? (
                <Button asChild size="lg" variant="outline" className="border-cream bg-cream/95 text-espresso hover:bg-cream hover:text-espresso">
                  <a href={`tel:${phone}`}><Phone className="mr-2 h-4 w-4" /> Call for instant quote</a>
                </Button>
              ) : (
                <Button asChild size="lg" variant="outline" className="border-cream bg-cream/95 text-espresso hover:bg-cream hover:text-espresso">
                  <Link to="/contact">Get an instant quote</Link>
                </Button>
              )}
            </div>
            <p className="mt-6 inline-block rounded-lg bg-espresso/40 px-4 py-2 text-base text-cream backdrop-blur-sm">
              Free delivery across Miami-Dade · All-in quote within the hour · No platform fees, ever.
            </p>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="relative z-10 mx-auto mt-10 max-w-7xl px-6 md:-mt-20">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { Icon: ShieldCheck, title: "Fully insured, FL-compliant", body: "Comprehensive coverage on every booking — never an upsell at the counter." },
            { Icon: MapPin, title: "Delivered across Miami-Dade", body: "MIA airport, South Beach hotels, Brickell condos, your Airbnb — I bring the car to you." },
            { Icon: Sparkles, title: "Detailed, fueled, beach-ready", body: "Every car cleaned inside and out, fueled up, and ready before you get the keys." },

          ].map(({ Icon, title, body }) => (
            <Card key={title} className="rounded-tr-3xl border-border/60 bg-card p-6 shadow-sm">
              <Icon className="h-7 w-7 text-accent" />
              <div className="mt-4 font-display text-xl text-espresso">{title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works — 3-step teaser */}
      <section className="mx-auto max-w-7xl px-6 pt-24 pb-4">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="text-sm uppercase tracking-[0.25em] text-accent">How it works</div>
            <h2 className="mt-2 font-display text-4xl text-espresso">From DM to driveway in three steps.</h2>
          </div>
          <Link to="/how-it-works" className="text-sm text-espresso/70 hover:text-accent">See the full process →</Link>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { num: "01", Icon: Search, title: "Browse", body: "Pick a car, your dates, and where you'd like it delivered — airport, hotel, or Airbnb." },
            { num: "02", Icon: MessageSquare, title: "Request", body: "Send a quick request. I confirm availability and reply with an all-in quote, usually within the hour." },
            { num: "03", Icon: Car, title: "Drive", body: "I deliver the car detailed, fueled, and ready. Insurance included. You drive — I'm a text away the whole trip." },

          ].map(({ num, Icon, title, body }) => (
            <Card key={num} className="relative rounded-tr-3xl border-border/60 bg-card p-6 shadow-sm">
              <div className="absolute right-5 top-4 font-display text-5xl text-accent/20">{num}</div>
              <Icon className="h-7 w-7 text-accent" />
              <div className="mt-4 font-display text-xl text-espresso">{title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Featured fleet */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-sm uppercase tracking-[0.25em] text-accent">Featured</div>
            <h2 className="mt-2 font-display text-4xl text-espresso">Recent arrivals</h2>
          </div>
          <Link to="/fleet" className="hidden md:inline text-sm text-espresso/70 hover:text-accent">View all →</Link>
        </div>
        {cars.length === 0 ? (
          <div className="mt-10 rounded-lg border border-dashed border-border bg-card p-10 text-center text-muted-foreground">
            New inventory arriving soon — check back shortly.
          </div>
        ) : (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            {cars.map(c => (
              <Link key={c.id} to="/cars/$carId" params={{ carId: c.id }} className="group overflow-hidden rounded-tr-3xl bg-card shadow-sm transition hover:shadow-md">
                <div className="aspect-[16/10] w-full overflow-hidden bg-muted sm:aspect-[4/3]">
                  {c.photos?.[0] ? (
                    <img
                      src={optimizeImg(c.photos[0], { width: 800 })}
                      srcSet={srcSet(c.photos[0], [400, 600, 900])}
                      sizes="(min-width: 768px) 33vw, 100vw"
                      alt={`${c.make} ${c.model}`}
                      width={800}
                      height={600}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  ) : <div className="grid h-full place-items-center text-muted-foreground">No image</div>}
                </div>
                <div className="p-5">
                  <div className="font-display text-xl text-espresso">{c.year} {c.make} {c.model}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{c.location || "Miami delivery available"}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-display text-2xl text-accent">{usd(Number(c.daily_rate))}</span>
                    <span className="text-sm text-muted-foreground">/ day</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        <div className="mt-8 text-center md:hidden">
          <Button asChild variant="outline" className="border-espresso/20 text-espresso">
            <Link to="/fleet">View the full fleet <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      {/* Who rents from Creed */}
      <section className="bg-secondary/40 border-y border-border/60">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="text-center">
            <div className="text-sm uppercase tracking-[0.25em] text-accent">Who rents from Creed</div>
            <h2 className="mt-2 font-display text-4xl text-espresso">Who's in the driver's seat.</h2>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { Icon: PartyPopper, title: "Bachelor & bachelorette trips", body: "Roll up in something the group will be talking about long after the weekend." },
              { Icon: Heart, title: "Couples & anniversaries", body: "A weekend to remember, delivered straight to your hotel." },
              { Icon: Briefcase, title: "Business travelers", body: "Skip the rental counter. I meet you at MIA on time, every time." },
              { Icon: CalendarDays, title: "Big events", body: "Weddings, F1, Art Basel, game weekends — book early, drive in style." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="rounded-tr-3xl bg-card p-6 shadow-sm">
                <Icon className="h-6 w-6 text-accent" />
                <div className="mt-4 font-display text-lg text-espresso">{title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/about" className="text-sm text-espresso/70 hover:text-accent">Read our story →</Link>

          </div>
        </div>
      </section>

      {/* Why Creed */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="text-sm uppercase tracking-[0.25em] text-accent text-center">Why Creed</div>
        <h2 className="mt-2 text-center font-display text-4xl text-espresso">Why people skip the big platforms.</h2>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { title: "You're talking to the owner", body: "No middleman, no support tickets. Faster answers, real flexibility, and a number that's actually mine." },
            { title: "Real concierge, not a counter", body: "Personal walk-through at delivery, 24/7 text support, and an owner who knows every car in the fleet." },
            { title: "The price you see is the price you pay", body: "Insurance included. No platform fees, no airport surcharges, no surprise upsells at the counter." },
          ].map(b => (
            <div key={b.title} className="rounded-tr-3xl bg-card p-6 shadow-sm border border-border/60">
              <div className="font-display text-xl text-espresso">{b.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/about" className="text-sm text-espresso/70 hover:text-accent">See the full Creed vs. Turo & Hertz comparison →</Link>
        </div>
      </section>


      {/* Testimonial strip */}
      <section className="mx-auto max-w-5xl px-6 pb-20 text-center">
        <div className="flex justify-center gap-1 text-accent">
          {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-5 w-5 fill-current" />)}
        </div>
        <blockquote className="mt-6 font-display text-2xl md:text-3xl text-espresso italic leading-snug">
          “Easiest luxury rental I've ever done in Miami. Car was spotless, delivered to my hotel, and the owner texted me through the whole trip.”
        </blockquote>
        <div className="mt-4 text-sm uppercase tracking-[0.2em] text-muted-foreground">— Recent guest, South Beach</div>
      </section>

      {/* Final CTA */}
      <section className="relative isolate overflow-hidden bg-espresso text-cream">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-[0.18]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, oklch(0.72 0.15 65) 0, transparent 45%), radial-gradient(circle at 80% 70%, oklch(0.72 0.15 65) 0, transparent 50%)",
          }}
        />
        <svg
          aria-hidden
          className="absolute -right-10 -top-10 -z-10 h-72 w-72 opacity-15 md:h-96 md:w-96"
          viewBox="0 0 100 100"
          fill="none"
          stroke="oklch(0.72 0.15 65)"
          strokeWidth="0.4"
        >
          <circle cx="50" cy="50" r="42" />
          <circle cx="50" cy="50" r="32" />
          <circle cx="50" cy="50" r="22" />
        </svg>
        <div className="mx-auto max-w-5xl px-6 py-24 text-center md:py-28">
          <div className="text-sm uppercase tracking-[0.3em] text-accent">Book your car</div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl">Ready when you are.</h2>
          <p className="mx-auto mt-4 max-w-xl text-cream/80">
            Pick a car, send your dates, I'll handle the rest. You'll have an all-in quote within the hour.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-accent text-espresso hover:bg-accent/90">
              <Link to="/fleet">Browse the fleet <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            {phone ? (
              <Button asChild size="lg" variant="outline" className="border-cream/40 bg-transparent text-cream hover:bg-cream/10 hover:text-cream">
                <a href={`tel:${phone}`}><Phone className="mr-2 h-4 w-4" /> Call {phone}</a>
              </Button>
            ) : (
              <Button asChild size="lg" variant="outline" className="border-cream/40 bg-transparent text-cream hover:bg-cream/10 hover:text-cream">
                <Link to="/contact">Get an instant quote</Link>
              </Button>
            )}
          </div>
        </div>
      </section>


      <SiteFooter />
    </div>
  );
}
